import React, { useState } from "react";
import { Link, useLocation } from "react-router";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Button,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Stack,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import HomeIcon from "@mui/icons-material/Home";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import SecurityIcon from "@mui/icons-material/Security";
import CellTowerIcon from "@mui/icons-material/CellTower";
import ApartmentIcon from "@mui/icons-material/Apartment";
import GavelIcon from "@mui/icons-material/Gavel";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import LoginIcon from "@mui/icons-material/Login";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../LanguageSwitcher";
import Brand from "./Brand";
import {
  appBarSx,
  pillItemSx,
  pillTrackSx,
  primaryActionSx,
  toolbarSx,
} from "./styles";
import { useLocale } from "src/i18n/LocaleProvider";
import { LOGIN_PATH } from "src/constants/paths";
import {
  audiencePath,
  blogPath,
  homePath,
  pricingPath,
  termsPath,
} from "src/i18n/paths";

const LoginAction: React.FC<{ fullWidth?: boolean; onNavigate?: () => void }> = ({
  fullWidth = false,
  onNavigate,
}) => {
  const { t } = useTranslation();

  return (
    <Button
      component={Link}
      to={LOGIN_PATH}
      onClick={onNavigate}
      variant="contained"
      fullWidth={fullWidth}
      startIcon={<LoginIcon fontSize="small" />}
      sx={primaryActionSx}
    >
      {t("nav.login")}
    </Button>
  );
};

export default function MarketingNavbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const { locale } = useLocale();

  const menuItems = [
    {
      label: t("nav.home"),
      path: homePath(locale),
      icon: <HomeIcon fontSize="small" />,
    },
    {
      label: t("nav.pricing"),
      path: pricingPath(locale),
      icon: <PaidOutlinedIcon fontSize="small" />,
    },
    {
      label: t("nav.cities"),
      path: audiencePath("cities", locale),
      icon: <ApartmentIcon fontSize="small" />,
    },
    {
      label: t("nav.localGov"),
      path: audiencePath("local-governments", locale),
      icon: <CellTowerIcon fontSize="small" />,
    },
    {
      label: t("nav.hoas"),
      path: audiencePath("hoas", locale),
      icon: <SecurityIcon fontSize="small" />,
    },
    {
      label: t("nav.blog"),
      path: blogPath(locale),
      icon: <MenuBookIcon fontSize="small" />,
    },
    {
      label: t("nav.terms"),
      path: termsPath(locale),
      icon: <GavelIcon fontSize="small" />,
    },
  ];

  const isActive = (path: string) => {
    if (path === homePath(locale)) {
      return location.pathname === path;
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  return (
    <>
      <AppBar position="sticky" elevation={0} sx={appBarSx}>
        <Toolbar sx={toolbarSx}>
          <Brand to={homePath(locale)} tagline={t("nav.tagline")} />

          <Box sx={{ ...pillTrackSx, display: { xs: "none", lg: "flex" } }}>
            {menuItems.map((item) => (
              <Button
                key={item.path}
                component={Link}
                to={item.path}
                startIcon={item.icon}
                sx={pillItemSx(isActive(item.path))}
              >
                {item.label}
              </Button>
            ))}
          </Box>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ display: { xs: "none", md: "flex" } }}
          >
            <LanguageSwitcher />
            <LoginAction />
          </Stack>

          <IconButton
            sx={{
              display: { md: "none" },
              bgcolor: "#0f172a",
              border: "1px solid rgba(30, 41, 59, 1)",
              borderRadius: "12px",
              color: "#cbd5e1",
            }}
            onClick={() => setMobileOpen(true)}
            aria-label={t("nav.toggleMenu")}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="top"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: "rgba(11, 15, 23, 0.97)",
            borderBottom: "1px solid rgba(30, 41, 59, 1)",
            pt: 2,
            pb: 3,
            px: 2,
          },
        }}
      >
        <Box px={1} mb={1}>
          <LanguageSwitcher />
        </Box>
        <List sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {menuItems.map((item) => (
            <ListItemButton
              key={item.path}
              component={Link}
              to={item.path}
              onClick={() => setMobileOpen(false)}
              sx={{
                borderRadius: "12px",
                bgcolor: isActive(item.path) ? "#7c3aed" : "transparent",
                color: isActive(item.path) ? "#fff" : "#cbd5e1",
                "&:hover": {
                  bgcolor: isActive(item.path) ? "#7c3aed" : "#0f172a",
                },
              }}
            >
              <ListItemIcon sx={{ color: "#a78bfa", minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          ))}
        </List>
        <Divider sx={{ my: 2, borderColor: "rgba(30, 41, 59, 1)" }} />
        <Box px={1}>
          <LoginAction fullWidth onNavigate={() => setMobileOpen(false)} />
        </Box>
      </Drawer>
    </>
  );
}
